import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Copy, Trash2, MoreVertical, Edit2, Film } from 'lucide-react';
import { Animation } from '../../../core/types/animation.types';
import { useAnimationStore } from '../../store/animationStore';
import { cn } from '../../utils/cn';

interface AnimationListItemProps {
  animation: Animation;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

export const AnimationListItem: React.FC<AnimationListItemProps> = ({
  animation,
  isSelected,
  onSelect,
}) => {
  const { updateAnimation, duplicateAnimation, deleteAnimation } = useAnimationStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(animation.name);
  const [showMenu, setShowMenu] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleClick = useCallback(() => {
    onSelect(animation.id);
  }, [animation.id, onSelect]);

  const handleDoubleClick = useCallback(() => {
    setIsEditing(true);
    setEditName(animation.name);
  }, [animation.name]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditName(e.target.value);
  }, []);

  const handleNameSubmit = useCallback(() => {
    const trimmedName = editName.trim();
    if (trimmedName && trimmedName !== animation.name) {
      updateAnimation(animation.id, { name: trimmedName });
    }
    setIsEditing(false);
  }, [animation.id, animation.name, editName, updateAnimation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleNameSubmit();
      } else if (e.key === 'Escape') {
        setEditName(animation.name);
        setIsEditing(false);
      }
    },
    [animation.name, handleNameSubmit]
  );

  const handleMenuClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu((prev) => !prev);
  }, []);

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowMenu(false);
      setIsEditing(true);
      setEditName(animation.name);
    },
    [animation.name]
  );

  const handleDuplicate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowMenu(false);
      duplicateAnimation(animation.id);
    },
    [animation.id, duplicateAnimation]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowMenu(false);
      deleteAnimation(animation.id);
    },
    [animation.id, deleteAnimation]
  );

  // Format duration to display
  const formatDuration = (seconds: number): string => {
    if (seconds < 1) {
      return `${Math.round(seconds * 1000)}ms`;
    }
    return `${seconds.toFixed(1)}s`;
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors group',
        'hover:bg-zinc-700/50',
        isSelected && 'bg-blue-600/30 hover:bg-blue-600/40'
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {/* Animation icon */}
      <Film
        size={14}
        className={cn('flex-shrink-0', isSelected ? 'text-blue-400' : 'text-zinc-400')}
      />

      {/* Name */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={handleNameChange}
            onBlur={handleNameSubmit}
            onKeyDown={handleKeyDown}
            className="w-full bg-zinc-900 text-zinc-100 text-sm px-1 py-0.5 rounded border border-blue-500 outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={cn(
              'text-sm truncate block',
              isSelected ? 'text-zinc-100' : 'text-zinc-300'
            )}
          >
            {animation.name}
          </span>
        )}
      </div>

      {/* Duration badge */}
      <span className="text-xs text-zinc-500 flex-shrink-0">
        {formatDuration(animation.duration)}
      </span>

      {/* Loop indicator */}
      {animation.loop && (
        <span className="text-xs text-zinc-500 flex-shrink-0" title="Loop enabled">
          L
        </span>
      )}

      {/* Context menu button */}
      <div className="relative" ref={menuRef}>
        <button
          className={cn(
            'p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity',
            'hover:bg-zinc-600',
            showMenu && 'opacity-100'
          )}
          onClick={handleMenuClick}
        >
          <MoreVertical size={14} className="text-zinc-400" />
        </button>

        {/* Dropdown menu */}
        {showMenu && (
          <div className="absolute right-0 top-full mt-1 z-50 bg-zinc-800 border border-zinc-700 rounded shadow-lg py-1 min-w-[120px]">
            <button
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
              onClick={handleEdit}
            >
              <Edit2 size={12} />
              Rename
            </button>
            <button
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
              onClick={handleDuplicate}
            >
              <Copy size={12} />
              Duplicate
            </button>
            <div className="border-t border-zinc-700 my-1" />
            <button
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-400 hover:bg-zinc-700 transition-colors"
              onClick={handleDelete}
            >
              <Trash2 size={12} />
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
